import { DisparityField } from 'libs/end-to-end/src/progress';
import { ProgressOutput } from 'libs/end-to-end/src/progress';
import { getLevelByColumnValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { ProgressExplanationAttributes } from 'libs/end-to-end/src/progress-explanation';
import { getMetricDisplayNameFromObservation } from 'libs/end-to-end/src/me';
import { getDimensionByColumnHeaderValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { ExplanationsOutputDictionary } from 'libs/end-to-end/src/explanations';
import { getProgressOverviewExplanationOutputKey } from 'libs/end-to-end/src/explanations-llm-types';

export const constructRateGroupsCopy = ({
  data,
  focusAreaDisplay,
  dimensionDisplay,
  metricDisplay,
}: {
  data: ProgressOutput;
  focusAreaDisplay: string;
  metricDisplay: string;
  dimensionDisplay: string;
}) => {
  const years = data.controlledDisparityGroups.map(({ name }) =>
    parseInt(name, 10),
  );
  const earliestYear = Math.min(...years);
  const latestYear = Math.max(...years);

  if (data.isMetricBoolean) {
    return `The chart shows ${focusAreaDisplay} rates by ${dimensionDisplay} for the years ${earliestYear} to ${latestYear}.`;
  }

  return `The chart shows average ${metricDisplay}s by ${dimensionDisplay} for the years ${earliestYear} to ${latestYear}.`;
};

export const constructRawDisparityCopy = ({
  data,
  focusAreaDisplay,
  dimensionDisplay,
  metricDisplay,
  referenceLevelDisplay,
}: {
  data: ProgressOutput;
  focusAreaDisplay: string;
  metricDisplay: string;
  dimensionDisplay: string;
  referenceLevelDisplay: string;
}) => {
  const years = data.controlledDisparityGroups.map(({ name }) =>
    parseInt(name, 10),
  );
  const earliestYear = Math.min(...years);
  const latestYear = Math.max(...years);

  const metricDisplayName = data.isMetricBoolean
    ? focusAreaDisplay
    : metricDisplay;

  return `The chart shows the raw ${metricDisplayName} disparities by ${dimensionDisplay} for the years ${earliestYear} to ${latestYear}, with ${referenceLevelDisplay} employees being the comparison group.`;
};

export const constructControlledDisparityCopy = ({
  data,
  focusAreaDisplay,
  dimensionDisplay,
  metricDisplay,
  referenceLevelDisplay,
}: {
  data: ProgressOutput;
  focusAreaDisplay: string;
  metricDisplay: string;
  dimensionDisplay: string;
  referenceLevelDisplay: string;
}) => {
  const years = data.controlledDisparityGroups.map(({ name }) =>
    parseInt(name, 10),
  );
  const earliestYear = Math.min(...years);
  const latestYear = Math.max(...years);

  const metricDisplayName = data.isMetricBoolean
    ? focusAreaDisplay
    : metricDisplay;

  return `The chart shows the controlled ${metricDisplayName} disparities by ${dimensionDisplay} for the years ${earliestYear} to ${latestYear}, with ${referenceLevelDisplay} employees being the comparison group.`;
};

export const constructProgressExplanationCopy = ({
  data,
  focusAreaDisplay,
  dimensionDisplay,
  metricDisplay,
  referenceLevelDisplay,
  selectedDisparity,
}: {
  data: ProgressOutput;
  focusAreaDisplay: string;
  metricDisplay: string;
  dimensionDisplay: string;
  referenceLevelDisplay: string;
  selectedDisparity: DisparityField;
}) => {
  const fnMap: Record<
    DisparityField,
    (props: {
      data: ProgressOutput;
      focusAreaDisplay: string;
      metricDisplay: string;
      dimensionDisplay: string;
      referenceLevelDisplay: string;
    }) => string
  > = {
    controlledDisparityGroups: constructControlledDisparityCopy,
    rawDisparityGroups: constructRawDisparityCopy,
    rateGroups: constructRateGroupsCopy,
  };

  return fnMap[selectedDisparity]({
    data,
    focusAreaDisplay,
    dimensionDisplay,
    metricDisplay,
    referenceLevelDisplay,
  });
};

export const getProgressPageExplanationAttributes = ({
  data,
  myObservation,
  comparisonGroup,
}: {
  data: ProgressOutput;
  myObservation: MyObservation | null;
  comparisonGroup: string;
}): ProgressExplanationAttributes => {
  const focusArea = data.focusArea;

  const metricDisplayName = getMetricDisplayNameFromObservation({
    focusArea,
    metricName: data.metric,
    myObservation,
  });

  const dimension = getDimensionByColumnHeaderValueFromObservation(
    myObservation,
    data.dimension,
  );

  const referenceLevel = getLevelByColumnValueFromObservation(
    myObservation,
    data.dimension,
    comparisonGroup,
  );

  return {
    focusAreaDisplay: focusArea.toLowerCase(),
    metricDisplay: metricDisplayName?.toLowerCase() ?? '',
    dimensionDisplay: dimension?.displayName.toLowerCase() ?? '',
    referenceLevelDisplay: referenceLevel?.displayName.toLowerCase() ?? '',
  };
};

export const getExplanationForProgress = ({
  explanations,
  data,
  focusAreaDisplay,
  dimensionDisplay,
  metricDisplay,
  referenceLevelDisplay,
  selectedDisparity,
}: {
  explanations: ExplanationsOutputDictionary | undefined;
  data: ProgressOutput;
  focusAreaDisplay: string;
  metricDisplay: string;
  dimensionDisplay: string;
  referenceLevelDisplay: string;
  selectedDisparity: DisparityField;
}) => {
  const fallbackExplanation = {
    overview: constructProgressExplanationCopy({
      data,
      focusAreaDisplay,
      metricDisplay,
      dimensionDisplay,
      referenceLevelDisplay,
      selectedDisparity,
    }),
    description: '',
  };

  if (!explanations) {
    return fallbackExplanation;
  }

  const explanationKey = Object.keys(explanations).find(
    (key) =>
      key ===
      getProgressOverviewExplanationOutputKey({
        disparity: selectedDisparity,
      }),
  );

  if (!explanationKey) {
    return fallbackExplanation;
  }

  const explanation = explanations[explanationKey];

  if (
    !explanation.overview &&
    !explanation.positiveDescription &&
    !explanation.negativeDescription
  ) {
    return fallbackExplanation;
  }

  const descriptionText = [
    explanation.positiveDescription,
    explanation.negativeDescription,
  ]
    .filter(Boolean)
    .join('\n');

  // We use the overview as the overview when it is present, but we will greedily take the description instead.
  // When we do this the description is kept empty.

  return {
    overview: explanation.overview ? explanation.overview : descriptionText,
    description: explanation.overview ? descriptionText : '',
  };
};
